import { useEffect, useState } from 'react';

/**
 * The current time, re-read on the top of each minute.
 *
 * One wake-up a minute rather than a tick a second, and each one re-armed
 * against the wall clock rather than scheduled on a fixed interval — a plain
 * `setInterval(…, 60_000)` accumulates whatever the event loop owes it and
 * slides off the minute boundary, so the displayed minute changes a beat late
 * and then later still.
 *
 * Lifted out of the taskbar clock when the iPod's Now Playing screen wanted
 * the same thing: two components showing the same minute should not be able to
 * disagree about when it turns over.
 */
export function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let timer = 0;

    const schedule = () => {
      const d = new Date();
      const untilNextMinute = 60_000 - (d.getSeconds() * 1000 + d.getMilliseconds());
      // The 20ms of slack lands the wake-up just *after* the boundary; without
      // it a timer that fires a millisecond early re-reads the old minute.
      timer = window.setTimeout(() => {
        setNow(new Date());
        schedule();
      }, untilNextMinute + 20);
    };

    schedule();
    return () => window.clearTimeout(timer);
  }, []);

  return now;
}
